import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Permission, UserRole } from '../types/user.types';
import DashboardShell from './dashboard/DashboardShell';
import { dashboardIconClass } from './dashboard/DashboardNav';
import SystemVersion from './SystemVersion';
import { themed } from '../ui/theme';

const linkBase =
  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-sky-300';

const iconBase = dashboardIconClass;

const navLinkClass = (isActive: boolean) =>
  `${linkBase} ${isActive
    ? themed({
        minimal: 'bg-white/15 text-white shadow-[inset_4px_0_0_rgb(var(--c-sidebar-accent))]',
        saas: 'bg-teal-50 text-teal-800 dark:bg-teal-900/35 dark:text-teal-100',
      })
    : themed({
        minimal: 'text-slate-100 hover:bg-white/10 hover:text-white',
        saas: 'text-gray-700 hover:bg-gray-100 dark:text-slate-200 dark:hover:bg-slate-800',
      })}`;

const groupButtonClass =
  `flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-sky-300 ${themed({
    minimal: 'text-slate-100 hover:bg-white/10 hover:text-white',
    saas: 'text-gray-700 hover:bg-gray-100 dark:text-slate-200 dark:hover:bg-slate-800',
  })}`;

const groupBorderClass = themed({
  minimal: 'border-white/15',
  saas: 'border-gray-200 dark:border-slate-700',
});

const sidebarHeaderClass = themed({
  minimal: 'dashboard-topbar-height flex items-center border-b border-white/15 px-4',
  saas: 'dashboard-topbar-height flex items-center border-b border-gray-200 px-4 dark:border-slate-800',
});

const sidebarTitleClass = themed({
  minimal: 'text-sm font-bold text-white',
  saas: 'text-sm font-bold text-gray-900 dark:text-white',
});

const sidebarSubtitleClass = themed({
  minimal: 'max-w-[200px] truncate text-xs text-blue-100',
  saas: 'max-w-[200px] truncate text-xs text-gray-500 dark:text-slate-400',
});

const sidebarFooterClass = themed({
  minimal: 'mt-auto shrink-0 border-t border-white/15 bg-sidebar-line/60 p-4',
  saas: 'mt-auto shrink-0 border-t border-gray-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900',
});

const logoutButtonClass = themed({
  minimal: 'w-full rounded-lg bg-white/10 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/20',
  saas: 'w-full rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-black dark:bg-teal-600 dark:hover:bg-teal-500',
});

const Caret: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    aria-hidden="true"
    className={`h-4 w-4 text-gray-500 transition-transform dark:text-slate-400 ${open ? 'rotate-180' : ''}`}
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.17l3.71-3.94a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06Z"
      clipRule="evenodd"
    />
  </svg>
);

const DashboardLayout: React.FC = () => {
  const [emendasOpen, setEmendasOpen] = useState(true);
  const [configEmendaOpen, setConfigEmendaOpen] = useState(true);
  const [planosOpen, setPlanosOpen] = useState(true);
  const [cadastrosOpen, setCadastrosOpen] = useState(true);
  const [relatoriosOpen, setRelatoriosOpen] = useState(true);
  const [usuariosOpen, setUsuariosOpen] = useState(true);
  const [sidebarHidden, setSidebarHidden] = useState(() => {
    if (typeof window === 'undefined') return false;
    if (window.matchMedia('(max-width: 767px)').matches) return true;
    return window.localStorage.getItem('dashboard:sidebarHidden') === 'true';
  });
  const { logout, user, hasPermission, hasAnyPermission, hasRole, sessionExpiring } = useAuth();
  const navigate = useNavigate();
  const role = String(user?.role ?? '').toUpperCase();

  useEffect(() => {
    if (!window.matchMedia('(max-width: 767px)').matches) {
      window.localStorage.setItem('dashboard:sidebarHidden', String(sidebarHidden));
    }
  }, [sidebarHidden]);

  // Read-only roles from Câmara Municipal - no admin/workflow/edit access
  const isReadOnly = role === 'CAMARA' || role === 'VEREADOR';
  const isLegislative = isReadOnly;
  const isGestorJuridico = role === 'GESTOR_JURIDICO';
  const isOperador = role === 'OPERADOR';
  const canAccessFilaJuridica = hasPermission(Permission.ACCESS_FILA_JURIDICA);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Usuários / Funcionalidades / Tipos de Documentos: os
  // recursos por trás desses links (UserAdminResource, AdminFuncionalidadeResource,
  // TiposDocumentoConfigPage) são @RolesAllowed(ADMIN)
  // hardcoded no backend, não role_permission configurável — VIEW_USERS nunca é
  // checado lá. Gatear pela permissão faria outro papel ver o link e cair em acesso
  // negado ao clicar; hasRole(ADMIN) é o que de fato abre essas telas.
  const hasUsuariosAccess = hasRole(UserRole.ADMIN);

  // Individual permission checks for menu items
  const canViewEmendas = hasPermission(Permission.VIEW_EMENDAS);
  const canViewExecucaoFinanceira = hasPermission(Permission.VIEW_EXECUCAO_FINANCEIRA);
  const canViewVisaoGeral = hasPermission(Permission.VIEW_EMENDAS_VISAO_GERAL);
  const canViewPreProjetos = hasPermission(Permission.VIEW_PRE_PROJETOS);
  const canViewGestaoPlanos = hasPermission(Permission.VIEW_GESTAO_PLANOS);
  const canViewPrestacaoContas = hasPermission(Permission.VIEW_PRESTACAO_CONTAS);
  const canViewInstitutions = hasPermission(Permission.VIEW_INSTITUTIONS);
  const canViewCouncilors = hasPermission(Permission.VIEW_COUNCILORS);
  const canViewEmendaReports = hasPermission(Permission.VIEW_EMENDA_REPORTS);
  const canViewInstitutionReports = hasPermission(Permission.VIEW_INSTITUTION_REPORTS);
  const canViewPreProjetoReports = hasPermission(Permission.VIEW_PRE_PROJETO_REPORTS);
  const canViewReports = canViewEmendaReports || canViewInstitutionReports || canViewPreProjetoReports;
  const canViewPendenciasReports = hasPermission(Permission.VIEW_PENDENCIAS_REPORTS);
  const canManageComissoes = hasPermission(Permission.MANAGE_COMISSOES);
  const canManageTaep = hasPermission(Permission.MANAGE_TAEP);
  const canManageTiposDocumento = hasPermission(Permission.MANAGE_TIPOS_DOCUMENTO);
  const canManageLeis = hasPermission(Permission.MANAGE_LEIS_ORCAMENTARIAS);
  const canViewProcuracoes = role === 'OPERADOR' || role === 'ADMIN';
  // Limites Parlamentares: o backend checa a permissão (role_permission), não o papel.
  const canManageParlamentarLimites = hasPermission(Permission.MANAGE_PARLAMENTAR_LIMITES);
  const canViewParlamentarLimites = canManageParlamentarLimites || hasPermission(Permission.VIEW_PARLAMENTAR_LIMITES);
  const canSolicitarAlteracao = hasPermission(Permission.CREATE_SOLICITACAO_ALTERACAO) || hasPermission(Permission.TRATAR_SOLICITACAO_ALTERACAO);

  // Grupos do menu "Gestão" divididos por assunto: emendas em análise,
  // configurações que orbitam a emenda (jurídico, documentos, limites, comissões),
  // planos/execução/prestação de contas, e cadastros de apoio (instituições/parlamentares)
  const hasEmendasGroupAccess = canViewEmendas || canViewPreProjetos || canViewVisaoGeral;
  const hasConfigEmendaGroupAccess = canAccessFilaJuridica || canManageTiposDocumento || canViewParlamentarLimites || canManageComissoes || canManageTaep || canManageLeis;
  const hasPlanosGroupAccess = canViewGestaoPlanos || canViewExecucaoFinanceira || canViewPrestacaoContas;
  const hasCadastrosGroupAccess = canViewInstitutions || canViewCouncilors;

  return (
    <DashboardShell
      sidebarHidden={sidebarHidden}
      setSidebarHidden={setSidebarHidden}
      sessionExpiring={sessionExpiring}
      onSessionLogin={logout}
      sidebar={(
        <>
          <div className={sidebarHeaderClass}>
            <div className="flex items-center gap-3">
              <img
                src="favicon.ico"
                alt="SIGEM"
                className="h-8 w-8 object-contain"
                onError={e => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 24 24' fill='none' stroke='%234f46e5' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z'/%3E%3Cpolyline points='9 22 9 12 15 12 15 22'/%3E%3C/svg%3E";
                }}
              />
              <div className="leading-tight">
                <div className={sidebarTitleClass}>SIGEM</div>
                <div className={sidebarSubtitleClass}>Sistema de Emendas</div>
              </div>
            </div>
          </div>

          <nav className="p-3 flex-1 overflow-y-auto" aria-label="Menu do dashboard">
            <ul className="space-y-1">
              <li>
                <NavLink
                  to="/dashboard"
                  end
                  className={({ isActive }) => navLinkClass(isActive)}
                >
                  <svg className={iconBase} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M3 13h8V3H3v10zm10 8h8V11h-8v10zM3 21h8v-6H3v6zm10-10h8V3h-8v8z" />
                  </svg>
                  <span>Área de Trabalho</span>
                </NavLink>
              </li>

              {canSolicitarAlteracao && (
                <li>
                  <NavLink to="/dashboard/solicitacoes" className={({ isActive }) => navLinkClass(isActive)}>
                    <svg className={iconBase} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M20 2H4a2 2 0 0 0-2 2v18l4-4h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2Zm-3 11H7v-2h10v2Zm0-3H7V8h10v2Z" />
                    </svg>
                    <span>{hasPermission(Permission.TRATAR_SOLICITACAO_ALTERACAO) ? 'Suporte e solicitações' : 'Fale com a equipe'}</span>
                  </NavLink>
                </li>
              )}

              {canViewProcuracoes && (
                <li>
                  <NavLink to="/dashboard/procuracoes" className={({ isActive }) => navLinkClass(isActive)}>
                    <svg className={iconBase} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M7 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8Zm10 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6ZM2 19a5 5 0 0 1 10 0v2H2v-2Zm11.5 2v-2a6.5 6.5 0 0 0-1.1-3.62A4.5 4.5 0 0 1 22 18v3h-8.5Z" />
                    </svg>
                    <span>Procurações</span>
                  </NavLink>
                </li>
              )}

              {/* Minha instituição — operador: telas escopadas às instituições vinculadas */}
              {isOperador && (
                <li className="pt-2">
                  <div className="px-3 pb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-fg-subtle">
                    Minha instituição
                  </div>
                  <ul className="space-y-1">
                    <li>
                      <NavLink to="/dashboard/instituicoes" className={({ isActive }) => navLinkClass(isActive)}>
                        <span>Instituições</span>
                      </NavLink>
                    </li>
                    {canViewPreProjetos && (
                      <li>
                        <NavLink to="/dashboard/pre-projetos" className={({ isActive }) => navLinkClass(isActive)}>
                          <span>Pré-projetos</span>
                        </NavLink>
                      </li>
                    )}
                    <li>
                      <NavLink to="/dashboard/emendas" end className={({ isActive }) => navLinkClass(isActive)}>
                        <span>Emendas</span>
                      </NavLink>
                    </li>
                    <li>
                      <NavLink to="/dashboard/planos" className={({ isActive }) => navLinkClass(isActive)}>
                        <span>Planos de Trabalho</span>
                      </NavLink>
                    </li>
                    <li>
                      <NavLink to="/dashboard/execucao-financeira" className={({ isActive }) => navLinkClass(isActive)}>
                        <span>Execução Financeira</span>
                      </NavLink>
                    </li>
                    <li>
                      <NavLink to="/dashboard/gestao/prestacao-contas-planos" className={({ isActive }) => navLinkClass(isActive)}>
                        <span>Prestação de Contas</span>
                      </NavLink>
                    </li>
                  </ul>
                </li>
              )}

              {/* Workflow - somente ADMIN (nunca para roles somente leitura) */}
              {isLegislative && (
                <li className="pt-2">
                  <div className="px-3 pb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-fg-subtle">
                    Consulta Legislativa
                  </div>
                  <ul className="space-y-1">
                    {canViewVisaoGeral && (
                      <li>
                        <NavLink
                          to="/dashboard/emendas/visao-geral"
                          className={({ isActive }) => navLinkClass(isActive)}
                        >
                          <span>Visão Geral</span>
                        </NavLink>
                      </li>
                    )}
                    {canViewPreProjetos && (
                      <li>
                        <NavLink to="/dashboard/pre-projetos" className={({ isActive }) => navLinkClass(isActive)}>
                          <span>Pré-projetos</span>
                        </NavLink>
                      </li>
                    )}
                    {canViewPreProjetos && (
                      <li>
                        <NavLink to="/dashboard/gestao-pre-projetos" className={({ isActive }) => navLinkClass(isActive)}>
                          <span>Seleção de Projetos</span>
                        </NavLink>
                      </li>
                    )}
                    {canViewEmendas && (
                      <li>
                        <NavLink
                          to="/dashboard/emendas"
                          className={({ isActive }) => navLinkClass(isActive)}
                        >
                          <span>Emendas</span>
                        </NavLink>
                      </li>
                    )}
                    {canViewExecucaoFinanceira && (
                      <li>
                        <NavLink
                          to="/dashboard/execucao-financeira"
                          className={({ isActive }) => navLinkClass(isActive)}
                        >
                          <span>Execução Financeira</span>
                        </NavLink>
                      </li>
                    )}
                    {canViewPrestacaoContas && (
                      <li>
                        <NavLink
                          to="/dashboard/gestao/prestacao-contas-planos"
                          className={({ isActive }) => navLinkClass(isActive)}
                        >
                          <span>Prestação de Contas</span>
                        </NavLink>
                      </li>
                    )}
                    {canViewParlamentarLimites && (
                      <li>
                        <NavLink
                          to="/dashboard/admin/parlamentar-limites"
                          className={({ isActive }) => navLinkClass(isActive)}
                        >
                          <span>Limites Parlamentares</span>
                        </NavLink>
                      </li>
                    )}
                    {canViewCouncilors && (
                      <li>
                        <NavLink
                          to="/dashboard/parlamentares"
                          className={({ isActive }) => navLinkClass(isActive)}
                        >
                          <span>Parlamentares</span>
                        </NavLink>
                      </li>
                    )}
                    {canViewInstitutions && (
                      <li>
                        <NavLink
                          to="/dashboard/instituicoes"
                          className={({ isActive }) => navLinkClass(isActive)}
                        >
                          <span>Instituições</span>
                        </NavLink>
                      </li>
                    )}
                    {canViewEmendaReports && (
                      <li>
                        <NavLink
                          to="/dashboard/relatorios/emendas"
                          className={({ isActive }) => navLinkClass(isActive)}
                        >
                          <span>Relatório de Emendas</span>
                        </NavLink>
                      </li>
                    )}
                    {canViewInstitutionReports && (
                      <li>
                        <NavLink
                          to="/dashboard/relatorios/instituicoes"
                          className={({ isActive }) => navLinkClass(isActive)}
                        >
                          <span>Relatório de Instituições</span>
                        </NavLink>
                      </li>
                    )}
                    {canViewPreProjetoReports && (
                      <li>
                        <NavLink
                          to="/dashboard/relatorios/pre-projetos"
                          className={({ isActive }) => navLinkClass(isActive)}
                        >
                          <span>Relatório de Pré-projetos</span>
                        </NavLink>
                      </li>
                    )}
                    {canViewPendenciasReports && (
                      <li>
                        <NavLink
                          to="/dashboard/relatorios/pendencias"
                          className={({ isActive }) => navLinkClass(isActive)}
                        >
                          <span>Relatório de Pendências</span>
                        </NavLink>
                      </li>
                    )}
                  </ul>
                </li>
              )}
              {/* Emendas (collapsible) - análise e acompanhamento das emendas em si */}
              {/* Operador usa a seção "Minha instituição" (escopada) em vez deste grupo genérico */}
              {!isReadOnly && !isOperador && hasEmendasGroupAccess && (
              <li>
                <button
                  type="button"
                  onClick={() => setEmendasOpen((v) => !v)}
                  className={groupButtonClass}
                  aria-expanded={emendasOpen}
                >
                  <span className="flex items-center gap-3">
                    <svg className={iconBase} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 000 2h6a1 1 0 100-2H6zm0 4a1 1 0 100 2h3a1 1 0 100-2H6z" clipRule="evenodd" />
                    </svg>
                    <span>Emendas</span>
                  </span>
                  <Caret open={emendasOpen} />
                </button>

                {emendasOpen && (
                  <ul className={`mt-1 ml-3 space-y-1 border-l pl-3 ${groupBorderClass}`}>
                    {canViewVisaoGeral && (
                    <li>
                      <NavLink
                        to="/dashboard/emendas/visao-geral"
                        className={({ isActive }) => navLinkClass(isActive)}
                      >
                        <span>Visão Geral</span>
                      </NavLink>
                    </li>
                    )}
                    {canViewPreProjetos && (
                    <li>
                      <NavLink to="/dashboard/pre-projetos" className={({ isActive }) => navLinkClass(isActive)}>
                        <span>Pré-projetos</span>
                      </NavLink>
                    </li>
                    )}
                    {canViewPreProjetos && (
                    <li>
                      <NavLink to="/dashboard/gestao-pre-projetos" className={({ isActive }) => navLinkClass(isActive)}>
                        <span>Seleção de Projetos</span>
                      </NavLink>
                    </li>
                    )}
                    {canViewEmendas && (
                    <li>
                      <NavLink
                        to="/dashboard/emendas"
                        end
                        className={({ isActive }) => navLinkClass(isActive)}
                      >
                        <span>Emendas</span>
                      </NavLink>
                    </li>
                    )}
                  </ul>
                )}
              </li>
              )}

              {/* Planos & Execução (collapsible) - plano de trabalho, execução financeira e prestação de contas */}
              {!isReadOnly && !isOperador && hasPlanosGroupAccess && (
              <li>
                <button
                  type="button"
                  onClick={() => setPlanosOpen((v) => !v)}
                  className={groupButtonClass}
                  aria-expanded={planosOpen}
                >
                  <span className="flex items-center gap-3">
                    <svg className={iconBase} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                    </svg>
                    <span>Planos & Execução</span>
                  </span>
                  <Caret open={planosOpen} />
                </button>

                {planosOpen && (
                  <ul className={`mt-1 ml-3 space-y-1 border-l pl-3 ${groupBorderClass}`}>
                    {canViewGestaoPlanos && (
                    <li>
                      <NavLink
                        to="/dashboard/gestao/planos-trabalho"
                        className={({ isActive }) => navLinkClass(isActive)}
                      >
                        <span>Planos de Trabalho</span>
                      </NavLink>
                    </li>
                    )}
                    {canViewExecucaoFinanceira && (
                    <li>
                      <NavLink
                        to="/dashboard/execucao-financeira"
                        className={({ isActive }) => navLinkClass(isActive)}
                      >
                        <span>Execução Financeira</span>
                      </NavLink>
                    </li>
                    )}
                    {canViewPrestacaoContas && (
                    <li>
                      <NavLink
                        to="/dashboard/gestao/prestacao-contas-planos"
                        className={({ isActive }) => navLinkClass(isActive)}
                      >
                        <span>Prestação de Contas</span>
                      </NavLink>
                    </li>
                    )}
                  </ul>
                )}
              </li>
              )}

              {/* Gestão (collapsible) - itens que orbitam o processo da emenda,
                  mas não são administração de sistema/usuários */}
              {!isReadOnly && !isOperador && hasConfigEmendaGroupAccess && (
              <li>
                <button
                  type="button"
                  onClick={() => setConfigEmendaOpen((v) => !v)}
                  className={groupButtonClass}
                  aria-expanded={configEmendaOpen}
                >
                  <span className="flex items-center gap-3">
                    <svg className={iconBase} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                    </svg>
                    <span>Gestão</span>
                  </span>
                  <Caret open={configEmendaOpen} />
                </button>

                {configEmendaOpen && (
                  <ul className={`mt-1 ml-3 space-y-1 border-l pl-3 ${groupBorderClass}`}>
                    {canAccessFilaJuridica && (
                    <li>
                      <NavLink
                        to="/dashboard/gestor-juridico"
                        className={({ isActive }) => navLinkClass(isActive)}
                      >
                        <span>Fila Jurídica</span>
                      </NavLink>
                    </li>
                    )}
                    {canManageTiposDocumento && (
                    <li>
                      <NavLink
                        to="/dashboard/tipos-documento-config"
                        className={({ isActive }) => navLinkClass(isActive)}
                      >
                        <span>Tipos de Documentos</span>
                      </NavLink>
                    </li>
                    )}
                    {canViewParlamentarLimites && (
                    <li>
                      <NavLink
                        to="/dashboard/admin/parlamentar-limites"
                        className={({ isActive }) => navLinkClass(isActive)}
                      >
                        <span>Limites Parlamentares</span>
                      </NavLink>
                    </li>
                    )}
                    {canManageComissoes && (
                    <li>
                      <NavLink
                        to="/dashboard/admin/comissoes"
                        className={({ isActive }) => navLinkClass(isActive)}
                      >
                        <span>Comissões de Seleção</span>
                      </NavLink>
                    </li>
                    )}
                    {canManageLeis && (
                    <li>
                      <NavLink
                        to="/dashboard/leis-orcamentarias"
                        className={({ isActive }) => navLinkClass(isActive)}
                      >
                        <span>Leis Orçamentárias</span>
                      </NavLink>
                    </li>
                    )}
                    {canManageTaep && (
                    <li>
                      <NavLink
                        to="/dashboard/taep"
                        className={({ isActive }) => navLinkClass(isActive)}
                      >
                        <span>Validador TAEP</span>
                      </NavLink>
                    </li>
                    )}
                  </ul>
                )}
              </li>
              )}

              {/* Cadastros (collapsible) - instituições e parlamentares */}
              {!isReadOnly && !isOperador && hasCadastrosGroupAccess && (
              <li>
                <button
                  type="button"
                  onClick={() => setCadastrosOpen((v) => !v)}
                  className={groupButtonClass}
                  aria-expanded={cadastrosOpen}
                >
                  <span className="flex items-center gap-3">
                    <svg className={iconBase} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12h1a1 1 0 110 2H3a1 1 0 110-2h1V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9zm-6 4h2v2H7v-2zm6 0h-2v2h2v-2z" clipRule="evenodd" />
                    </svg>
                    <span>Cadastros</span>
                  </span>
                  <Caret open={cadastrosOpen} />
                </button>

                {cadastrosOpen && (
                  <ul className={`mt-1 ml-3 space-y-1 border-l pl-3 ${groupBorderClass}`}>
                    {canViewInstitutions && (
                    <li>
                      <NavLink
                        to="/dashboard/instituicoes"
                        className={({ isActive }) => navLinkClass(isActive)}
                      >
                        <span>Instituições</span>
                      </NavLink>
                    </li>
                    )}
                    {canViewCouncilors && (
                    <li>
                      <NavLink
                        to="/dashboard/parlamentares"
                        className={({ isActive }) => navLinkClass(isActive)}
                      >
                        <span>Parlamentares</span>
                      </NavLink>
                    </li>
                    )}
                  </ul>
                )}
              </li>
              )}

              {/* Relatórios (collapsible) */}
              {!isReadOnly && (canViewReports || canViewPendenciasReports) && (
              <li>
                <button
                  type="button"
                  onClick={() => setRelatoriosOpen((v) => !v)}
                  className={groupButtonClass}
                  aria-expanded={relatoriosOpen}
                >
                  <span className="flex items-center gap-3">
                    <svg className={iconBase} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M5 4a2 2 0 0 0-2 2v11a3 3 0 0 0 3 3h13a2 2 0 0 0 2-2V8.83a2 2 0 0 0-.59-1.41l-2.83-2.83A2 2 0 0 0 16.17 4H5Zm2 5h10v2H7V9Zm0 4h10v2H7v-2Zm0 4h6v2H7v-2Z" />
                    </svg>
                    <span>Relatórios</span>
                  </span>
                  <Caret open={relatoriosOpen} />
                </button>

                {relatoriosOpen && (
                  <ul className={`mt-1 ml-3 space-y-1 border-l pl-3 ${groupBorderClass}`}>
                    {canViewEmendaReports && (
                    <li>
                      <NavLink
                        to="/dashboard/relatorios/emendas"
                        className={({ isActive }) => navLinkClass(isActive)}
                      >
                        <span>Emendas</span>
                      </NavLink>
                    </li>
                    )}
                    {canViewInstitutionReports && (
                    <li>
                      <NavLink
                        to="/dashboard/relatorios/instituicoes"
                        className={({ isActive }) => navLinkClass(isActive)}
                      >
                        <span>Instituições</span>
                      </NavLink>
                    </li>
                    )}
                    {canViewPreProjetoReports && (
                    <li>
                      <NavLink
                        to="/dashboard/relatorios/pre-projetos"
                        className={({ isActive }) => navLinkClass(isActive)}
                      >
                        <span>Pré-projetos</span>
                      </NavLink>
                    </li>
                    )}
                    {canViewPendenciasReports && (
                    <li>
                      <NavLink
                        to="/dashboard/relatorios/pendencias"
                        className={({ isActive }) => navLinkClass(isActive)}
                      >
                        <span>Pendências</span>
                      </NavLink>
                    </li>
                    )}
                  </ul>
                )}
              </li>
              )}

              {hasUsuariosAccess && (
              <li>
                <button
                  type="button"
                  onClick={() => setUsuariosOpen((v) => !v)}
                  className={groupButtonClass}
                  aria-expanded={usuariosOpen}
                >
                  <span className="flex items-center gap-3">
                    <svg className={iconBase} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M16 11c1.66 0 3-1.34 3-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zM8 11c1.66 0 3-1.34 3-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5C15 14.17 10.33 13 8 13zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.95 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
                    </svg>
                    <span>Administrador</span>
                  </span>
                  <Caret open={usuariosOpen} />
                </button>

                {usuariosOpen && (
                  <ul className={`mt-1 ml-3 space-y-1 border-l pl-3 ${groupBorderClass}`}>
                    {hasRole(UserRole.ADMIN) && (
                    <li>
                      <NavLink
                        to="/dashboard/workflow"
                        className={({ isActive }) => navLinkClass(isActive)}
                      >
                        <span>Fluxo de Tramitação</span>
                      </NavLink>
                    </li>
                    )}
                    <li>
                      <NavLink
                        to="/dashboard/usuarios"
                        className={({ isActive }) => navLinkClass(isActive)}
                      >
                        <span>Usuários</span>
                      </NavLink>
                    </li>
                    <li>
                      <NavLink
                        to="/dashboard/admin/funcionalidades"
                        className={({ isActive }) => navLinkClass(isActive)}
                      >
                        <span>Funcionalidades</span>
                      </NavLink>
                    </li>
                    {hasRole(UserRole.ADMIN) && (
                    <li>
                      <NavLink
                        to="/dashboard/admin/permissoes"
                        className={({ isActive }) => navLinkClass(isActive)}
                      >
                        <span>Permissões</span>
                      </NavLink>
                    </li>
                    )}
                    {hasRole(UserRole.ADMIN) && (
                    <li>
                      <NavLink
                        to="/dashboard/admin/emails"
                        className={({ isActive }) => navLinkClass(isActive)}
                      >
                        <span>Gerenciamento de E-mails</span>
                      </NavLink>
                    </li>
                    )}
                    {hasRole(UserRole.ADMIN) && (
                    <li>
                      <NavLink
                        to="/dashboard/admin/parametros"
                        className={({ isActive }) => navLinkClass(isActive)}
                      >
                        <span>Parâmetros do Sistema</span>
                      </NavLink>
                    </li>
                    )}
                  </ul>
                )}
              </li>
              )}
            </ul>
          </nav>

          <div className={sidebarFooterClass}>
            <button
              type="button"
              onClick={handleLogout}
              className={logoutButtonClass}
            >
              Sair
            </button>
            <SystemVersion />
          </div>
        </>
      )}
    >
      <Outlet />
    </DashboardShell>
  );
};

export default DashboardLayout;


